import { useEffect, useMemo, useState, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import { api } from '../api/client';
import { cachedFetch, invalidate } from '../api/cache.js';
import { useAuth } from '../context/AuthContext.jsx';
import { useWorkspace } from '../context/WorkspaceContext.jsx';
import { Badge, Empty, STATUS_TONE, PRIORITY_TONE, fmtDate, daysUntil, label, SkeletonTable } from '../components/ui.jsx';
import { useToast } from '../components/feedback.jsx';
import { t } from '../i18n/index.jsx';
import TaskForm, { blankTask, TASK_PRIORITIES, TASK_STATUSES } from '../components/forms/TaskForm.jsx';
import { IconPlus } from '../components/icons.jsx';
import TaskKanban from '../components/TaskKanban.jsx';
import ExportButton from '../components/ExportButton.jsx';
import AppCalendar from '../components/AppCalendar.jsx';
import TaskTimeline from '../components/TaskTimeline.jsx';

const VIEWS = ['list', 'kanban', 'calendar', 'gantt'];

function taskContextLabel(task) {
  if (task.fund_client_name || task.fund_name) {
    return [task.fund_client_name, task.fund_name].filter(Boolean).join(' · ') || '—';
  }
  return task.project_name || '—';
}

function TaskList({ tasks, onOpen, selected, onToggle, onToggleAll, canSelect, contextHeader }) {
  const sorted = useMemo(() => [...tasks].sort((a, b) => {
    if (a.status === 'done' && b.status !== 'done') return 1;
    if (b.status === 'done' && a.status !== 'done') return -1;
    if (!a.due_date && !b.due_date) return 0;
    if (!a.due_date) return 1;
    if (!b.due_date) return -1;
    return a.due_date.localeCompare(b.due_date);
  }), [tasks]);

  if (sorted.length === 0) {
    return <Empty>{t('No tasks match these filters. Create one with the "New task" button.')}</Empty>;
  }

  const allSelected = sorted.length > 0 && sorted.every((task) => selected.has(task.id));

  return (
    <div className="table-wrap panel" style={{ padding: 0 }}>
      <table className="table task-table">
        <thead>
          <tr>
            {canSelect && (
              <th className="col-check">
                <input
                  type="checkbox"
                  checked={allSelected}
                  onChange={() => onToggleAll(sorted)}
                  aria-label={t('Select all')}
                />
              </th>
            )}
            <th>{t('Task')}</th>
            <th>{contextHeader || t('Project')}</th>
            <th>{t('Assignee')}</th>
            <th>{t('Priority')}</th>
            <th>{t('Status')}</th>
            <th>{t('Due')}</th>
          </tr>
        </thead>
        <tbody>
          {sorted.map((task) => {
            const d = daysUntil(task.due_date);
            const overdue = d != null && d < 0 && task.status !== 'done';
            return (
              <tr
                key={task.id}
                className={`${onOpen ? 'clickable' : ''} ${selected.has(task.id) ? 'row-selected' : ''}`}
                onClick={() => onOpen?.(task)}
              >
                {canSelect && (
                  <td className="col-check" onClick={(e) => e.stopPropagation()}>
                    <input
                      type="checkbox"
                      checked={selected.has(task.id)}
                      onChange={() => onToggle(task.id)}
                      aria-label={t('Select task')}
                    />
                  </td>
                )}
                <td className="ll-title">{task.title}</td>
                <td className="muted">{taskContextLabel(task)}</td>
                <td className="muted">{task.assignee_name || '—'}</td>
                <td><Badge tone={PRIORITY_TONE[task.priority]}>{label(task.priority)}</Badge></td>
                <td><Badge tone={STATUS_TONE[task.status]}>{label(task.status)}</Badge></td>
                <td>
                  <span className={`due ${overdue ? 'overdue' : d != null && d <= 2 && task.status !== 'done' ? 'soon' : ''}`}>
                    {task.due_date
                      ? overdue
                        ? t('{n}d overdue', { n: -d })
                        : d === 0 ? t('today') : fmtDate(task.due_date)
                      : '—'}
                  </span>
                </td>
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}

function readView(params) {
  const v = params.get('view');
  if (VIEWS.includes(v)) return v;
  try {
    const saved = localStorage.getItem('tasks_view');
    if (VIEWS.includes(saved)) return saved;
  } catch { /* private mode */ }
  return 'list';
}

export default function Tasks() {
  const { user } = useAuth();
  const { workspace, isFunds, isTrade, isGeneral } = useWorkspace();
  const toast = useToast();
  const canEdit = user.role === 'admin' || user.role === 'manager';
  const [searchParams, setSearchParams] = useSearchParams();
  const [tasks, setTasks] = useState(null);
  const [projects, setProjects] = useState([]);
  const [fundClients, setFundClients] = useState([]);
  const [users, setUsers] = useState([]);
  const [editing, setEditing] = useState(null);
  const [selected, setSelected] = useState(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);

  const view = readView(searchParams);
  const fProject = searchParams.get('project') || '';
  const fFundClient = searchParams.get('fund_client') || '';
  const fAssignee = searchParams.get('assignee') || '';
  const fPriority = searchParams.get('priority') || '';
  const mine = searchParams.get('mine') === '1';

  const patchParams = useCallback((patch) => {
    setSearchParams((prev) => {
      const next = new URLSearchParams(prev);
      Object.entries(patch).forEach(([k, v]) => {
        if (v == null || v === '' || v === false) next.delete(k);
        else next.set(k, String(v));
      });
      return next;
    }, { replace: true });
  }, [setSearchParams]);

  function switchView(v) {
    try { localStorage.setItem('tasks_view', v); } catch { /* private mode */ }
    patchParams({ view: v === 'list' ? null : v });
    setSelected(new Set());
  }

  function setFilter(key, value) {
    if (key === 'assignee') patchParams({ assignee: value || null, mine: null });
    else if (key === 'mine') patchParams({ mine: value ? '1' : null, assignee: null });
    else patchParams({ [key]: value || null });
  }

  function clearFilters() {
    patchParams({ project: null, fund_client: null, assignee: null, priority: null, mine: null });
  }

  async function load({ force = false } = {}) {
    const path = `/tasks?workspace=${workspace}`;
    const res = await cachedFetch(path, () => api.get(path), { force, ttl: 30_000 });
    setTasks(res.data);
  }
  useEffect(() => {
    setTasks(null);
    setSelected(new Set());
    load({ force: true });
    cachedFetch('/users', () => api.get('/users')).then((r) => setUsers(r.data)).catch(() => setUsers([]));
    if (isFunds) {
      cachedFetch('/fund-clients', () => api.get('/fund-clients')).then((r) => setFundClients(r.data)).catch(() => setFundClients([]));
      setProjects([]);
    } else {
      // Trade and General both filter by project — each sees only its own line.
      const p = `/projects?workspace=${isGeneral ? 'general' : 'trade'}`;
      cachedFetch(p, () => api.get(p)).then((r) => setProjects(r.data)).catch(() => setProjects([]));
      setFundClients([]);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [workspace, isTrade, isFunds, isGeneral]);

  const effectiveAssignee = mine ? String(user.id) : fAssignee;

  const filtered = useMemo(() => (tasks || []).filter((task) =>
    (!fProject || String(task.project_id) === fProject) &&
    (!fFundClient || String(task.fund_client_id) === fFundClient) &&
    (!effectiveAssignee || String(task.assignee_id) === effectiveAssignee) &&
    (!fPriority || task.priority === fPriority)
  ), [tasks, fProject, fFundClient, effectiveAssignee, fPriority]);

  function onStatusChange(id, status) {
    setTasks((ts) => ts.map((task) => (task.id === Number(id) ? { ...task, status } : task)));
  }

  function toggle(id) {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }

  function toggleAll(rows) {
    setSelected((prev) => {
      const allOn = rows.length > 0 && rows.every((r) => prev.has(r.id));
      if (allOn) return new Set();
      return new Set(rows.map((r) => r.id));
    });
  }

  async function applyBulk({ status, assignee_id } = {}) {
    const ids = [...selected];
    if (!ids.length) return;
    setBulkBusy(true);
    try {
      const body = { ids };
      if (status) body.status = status;
      if (assignee_id !== undefined) body.assignee_id = assignee_id === '' ? null : Number(assignee_id);
      await api.post('/tasks/bulk', body);
      invalidate(`/tasks?workspace=${workspace}`);
      invalidate('/tasks');
      setSelected(new Set());
      await load({ force: true });
      toast(t('Updated {n} tasks', { n: ids.length }));
    } catch (e) {
      toast(e.message, { tone: 'error' });
    } finally {
      setBulkBusy(false);
    }
  }

  const hasFilters = !!(fProject || fFundClient || fAssignee || fPriority || mine);
  const VIEW_LABELS = { list: t('List'), kanban: t('Board'), calendar: t('Calendar'), gantt: t('Timeline') };
  const contextHeader = isFunds ? t('Fund client') : isGeneral ? t('Initiative') : t('Project');
  const exportCols = [
    { header: t('Task'), field: 'title' },
    { header: contextHeader, value: (r) => taskContextLabel(r) },
    { header: t('Assignee'), field: 'assignee_name' },
    { header: t('Priority'), field: 'priority' },
    { header: t('Status'), field: 'status' },
    { header: t('Due'), field: 'due_date' },
  ];

  return (
    <div className="page fade-in">
      <div className="page-head">
        <h1 className="page-title">{t('Tasks')}</h1>
        <div style={{ display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' }}>
          <div className="sheet-tabs" style={{ margin: 0 }}>
            {VIEWS.map((v) => (
              <button key={v} type="button" className={`chip ${view === v ? 'on' : ''}`} onClick={() => switchView(v)}>
                {VIEW_LABELS[v]}
              </button>
            ))}
          </div>
          <ExportButton filename="tasks" rows={filtered} columns={exportCols} disabled={!filtered.length} />
          {canEdit && (
            <button
              type="button"
              className="btn primary"
              onClick={() => setEditing({
                ...blankTask,
                ...(isFunds ? { fund_client_id: fFundClient || '', project_id: '' } : { project_id: fProject || '', fund_client_id: '' }),
              })}
            >
              <IconPlus width={15} height={15} /> {t('New task')}
            </button>
          )}
        </div>
      </div>

      <div className="toolbar">
        <button
          type="button"
          className={`chip ${mine ? 'on' : ''}`}
          onClick={() => setFilter('mine', !mine)}
          title={t('Show only tasks assigned to me')}
        >
          {t('My tasks')}
        </button>
        {!isFunds && (
          <select value={fProject} onChange={(e) => setFilter('project', e.target.value)}>
            <option value="">{isGeneral ? t('All initiatives') : t('All projects')}</option>
            {projects.map((p) => <option key={p.id} value={String(p.id)}>{p.name}</option>)}
          </select>
        )}
        {isFunds && (
          <select value={fFundClient} onChange={(e) => setFilter('fund_client', e.target.value)}>
            <option value="">{t('All fund clients')}</option>
            {fundClients.map((fc) => (
              <option key={fc.id} value={String(fc.id)}>
                {fc.client_name}{fc.fund_name ? ` · ${fc.fund_name}` : ''}
              </option>
            ))}
          </select>
        )}
        <select
          value={mine ? String(user.id) : fAssignee}
          onChange={(e) => setFilter('assignee', e.target.value)}
          disabled={mine}
        >
          <option value="">{t('All assignees')}</option>
          {users.map((u) => <option key={u.id} value={String(u.id)}>{u.name}</option>)}
        </select>
        <select value={fPriority} onChange={(e) => setFilter('priority', e.target.value)}>
          <option value="">{t('All priorities')}</option>
          {TASK_PRIORITIES.map((p) => <option key={p} value={p}>{label(p)}</option>)}
        </select>
        {hasFilters && (
          <button type="button" className="btn ghost sm" onClick={clearFilters}>{t('Clear')}</button>
        )}
        {tasks && (
          <span className="muted sm filter-count">
            {t('{n} shown', { n: filtered.length })}
            {hasFilters ? ` / ${tasks.length}` : ''}
            {' · '}
            {isFunds ? t('Funds') : isGeneral ? t('General') : t('Trade')}
          </span>
        )}
      </div>

      {canEdit && selected.size > 0 && view === 'list' && (
        <div className="bulk-bar">
          <strong>{t('{n} selected', { n: selected.size })}</strong>
          <select
            defaultValue=""
            disabled={bulkBusy}
            onChange={(e) => {
              const v = e.target.value;
              e.target.value = '';
              if (v) applyBulk({ status: v });
            }}
          >
            <option value="">{t('Set status…')}</option>
            {TASK_STATUSES.map((s) => <option key={s} value={s}>{label(s)}</option>)}
          </select>
          <select
            defaultValue=""
            disabled={bulkBusy}
            onChange={(e) => {
              const v = e.target.value;
              e.target.value = '';
              if (v !== '') applyBulk({ assignee_id: v === '__none__' ? '' : v });
            }}
          >
            <option value="">{t('Assign to…')}</option>
            <option value="__none__">{t('Unassigned')}</option>
            {users.map((u) => <option key={u.id} value={String(u.id)}>{u.name}</option>)}
          </select>
          <button type="button" className="btn ghost sm" onClick={() => setSelected(new Set())} disabled={bulkBusy}>
            {t('Clear selection')}
          </button>
        </div>
      )}

      {!tasks ? (
        <SkeletonTable rows={8} cols={6} />
      ) : (
        <>
          {view === 'list' && (
            <TaskList
              tasks={filtered}
              onOpen={canEdit ? setEditing : null}
              selected={selected}
              onToggle={toggle}
              onToggleAll={toggleAll}
              canSelect={canEdit}
              contextHeader={contextHeader}
            />
          )}

          {view === 'kanban' && (
            <TaskKanban
              tasks={filtered}
              canEdit={canEdit}
              onCardClick={setEditing}
              onStatusChange={onStatusChange}
            />
          )}

          {view === 'calendar' && (
            <AppCalendar
              data={filtered}
              tasksOnly
              canEdit={canEdit}
              onEventOpen={(ev) => {
                const raw = tasks.find((task) => task.id === ev.RawId);
                if (raw) setEditing(raw);
              }}
              onDatesChanged={() => { invalidate(`/tasks?workspace=${workspace}`); load({ force: true }); }}
            />
          )}

          {view === 'gantt' && (
            <TaskTimeline
              tasks={filtered}
              onTaskClick={setEditing}
            />
          )}
        </>
      )}

      {editing && (
        <TaskForm
          initial={editing}
          projects={projects}
          fundClients={fundClients}
          users={users}
          workspace={workspace}
          onClose={() => setEditing(null)}
          onSaved={() => { invalidate('/tasks'); setEditing(null); load({ force: true }); }}
        />
      )}
    </div>
  );
}
